import * as React from 'react'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import * as XStateReact from '@xstate/react'
import * as Root from '../contexts/root'
import * as Messages from 'constants/min/messages'
import { ButtonSet, Previous } from './Navigation'
import { Tag, TagSet } from 'components/tag'
import { DateTime } from 'components/time'
import * as Buttons from 'components/button'
import * as Hooks from 'hooks'
import * as Texts from 'components/text'
import * as Auth from 'contexts/authentication'
import MINColors from 'styles/min/observations.module.css'
import Colors from 'styles/colors.module.css'
import styles from './Submit.module.css'
import * as Anchors from './Anchors'

export default function Submit() {
    const description = 'Mountain Information Network - Form / Submit Screen'
    const [state] = useActor()

    return (
        <section>
            <header>
                <Title />
            </header>
            <ConnectionStatus />
            <StepSet>
                <AuthorizationStep />
                <SubmissionStep />
            </StepSet>
            <ButtonSet>
                <Previous disabled={state.done}>
                    <FormattedMessage
                        defaultMessage="Preview my report"
                        description={description}
                    />
                </Previous>
            </ButtonSet>
        </section>
    )
}

// Utils
function Title() {
    const description = 'Mountain Information Network - Form / Submit Screen'
    const [state] = useActor()
    const online = state.matches('online')

    return (
        <h2>
            {online ? (
                <FormattedMessage
                    description={description}
                    defaultMessage="Your observations are being submitted..."
                />
            ) : (
                <React.Fragment>
                    <FormattedMessage
                        description={description}
                        defaultMessage="Your observations can not be submitted!"
                    />
                    <small>
                        <FormattedMessage
                            description={description}
                            defaultMessage="Make sure to be online and have a good connection to send your observations."
                        />
                    </small>
                </React.Fragment>
            )}
        </h2>
    )
}
function ConnectionStatus() {
    const description = 'Mountain Information Network - Form / Submit'
    const [state] = useActor()
    const online = state.matches('online')

    if (online) {
        return null
    }

    return (
        <label className={styles.ConnectionStatus}>
            <span>
                <FormattedMessage description={description} defaultMessage="Your are currently" />
            </span>
            <Tag as="output" className={Colors.error}>
                <FormattedMessage description={description} defaultMessage="Offline" />
            </Tag>
            <ConnectionTag />
        </label>
    )
}

// Steps
function AuthorizationStep() {
    const description = 'MIN Form / Submit / Authorization'
    const username = Auth.useUsername()
    const tag = (
        <Tag as="span">
            <FormattedMessage defaultMessage="Authorization" description={description} />
        </Tag>
    )
    let message = (
        <FormattedMessage
            description={description}
            defaultMessage="Not authorized yet"
            values={{ username }}
        />
    )

    if (typeof username === 'string') {
        message = (
            <FormattedMessage
                description={description}
                defaultMessage="Authenticated as {username}"
                values={{ username }}
            />
        )
    }

    return (
        <Step name="authorization" tags={tag}>
            <p>{message}</p>
        </Step>
    )
}
function SubmissionStep() {
    const description = 'Mountain Information Network - Form / Submit'
    const { datetime, title, observations, images } = Root.useCurrentValues()
    const tag = (
        <Tag as="span">
            <FormattedMessage defaultMessage="Submission" description={description} />
        </Tag>
    )

    return (
        <Step name="submission" tags={tag}>
            <h2>
                {title}
                {Array.isArray(images) && (
                    <Texts.Muted as="small">
                        <FormattedMessage
                            description={description}
                            defaultMessage="{count, plural, one {# photo} other {# photos}} attached"
                            values={{ count: images.length }}
                        />
                    </Texts.Muted>
                )}
            </h2>
            <DateTime value={datetime} />
            <TagSet>
                {Object.keys(observations).map(type => (
                    <Tag key={type} className={MINColors[type]}>
                        <FormattedMessage {...Messages.Names[type]} />
                    </Tag>
                ))}
            </TagSet>
        </Step>
    )
}

// Base components
function StepSet({ children }) {
    return <ol className={styles.StepSet}>{children}</ol>
}
function Step({ name, tags, children }) {
    const [state] = useActor()
    const active = state.matches('online.' + name)
    const status = getStatus(state, name)
    const className = clsx(styles.Step, styles[status], {
        [styles.Active]: active,
    })

    return (
        <li className={className}>
            {tags}
            {children}
            {status === REJECTED && <ErrorMessage />}
        </li>
    )
}
function ErrorMessage() {
    const [state, send] = useActor()
    const description = 'Mountain Information Network - Form / Submit'
    let message
    function retry() {
        send('RETRY')
    }

    switch (true) {
        case state.matches('online.authorization'):
            message = (
                <FormattedMessage
                    description={description}
                    defaultMessage="An error occured while authenticating."
                />
            )
            break
        case state.matches('online.submission'):
            message = (
                <FormattedMessage
                    description={description}
                    defaultMessage="An error occured while submitting your observations."
                />
            )
            break
        default:
            message = null
    }

    return (
        <div className={styles.ErrorMessage}>
            <p>{message}</p>
            <Buttons.ButtonSet>
                {state.nextEvents.includes('RETRY') && (
                    <Buttons.Button onClick={retry}>
                        <FormattedMessage defaultMessage="Try again" description={description} />
                    </Buttons.Button>
                )}
                {state.context?.retries > 3 ? <ContactUs /> : null}
            </Buttons.ButtonSet>
        </div>
    )
}
function ContactUs() {
    const description = 'Mountain Information Network - Form / Submit / Contact us'
    const intl = useIntl()
    const [state] = useActor()
    const { error } = state.context
    const subject = intl.formatMessage(
        {
            description,
            defaultMessage: 'Error while {message}',
        },
        {
            message: state.toStrings().pop(),
        }
    )
    const body = JSON.stringify(error.payload)

    return <Anchors.ContactUs subject={subject} body={body} />
}
function ConnectionTag() {
    const description = 'MIN Form / Submit '
    const connection = Hooks.useNetworkInformation()

    if (typeof connection?.effectiveType !== 'string') {
        return null
    }

    const effectiveType = connection.effectiveType.toLowerCase()

    switch (true) {
        case effectiveType.includes('2g'):
            return (
                <Tag as="ouput" className={Colors.warning}>
                    <FormattedMessage description={description} defaultMessage="Slow connection" />
                </Tag>
            )
        case effectiveType === '3g':
            return (
                <Tag as="ouput" className={Colors.warning}>
                    <FormattedMessage
                        description={description}
                        defaultMessage="Decent connection"
                    />
                </Tag>
            )
        default:
            return null
    }
}

// Utils
function useActor() {
    const [state] = Root.useService()

    return XStateReact.useActor(state.children.submit)
}
function getStatus(state, name) {
    const parent = 'online.' + name
    const active = state.matches(parent)

    if (active) {
        switch (true) {
            case state.matches(parent + '.success'):
                return RESOLVED
            case state.matches(parent + '.failure'):
                return REJECTED
            case state.matches(parent + '.pending'):
                return PENDING
            default:
                return PENDING
        }
    }

    return state.context.completed.has(name) ? RESOLVED : IDLE
}

// Constants
const IDLE = 'Idle'
const RESOLVED = 'Resolved'
const REJECTED = 'Rejected'
const PENDING = 'Pending'
